import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import {
  useGetUsersListLazyQuery,
  useGetUsersListQuery,
  UserRole,
} from 'gql/types';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SAVE_APP_SETTINGS_MUTATION } from '../../graphql/mutations';
import { GET_APP_SETTINGS_QUERY } from '../../graphql/queries';

const { Title, Text } = Typography;

type FfpType = 'None' | 'SGL' | 'DBL';

interface IDonationFailureWeight {
  minMl: number;
  maxMl: number;
  pRBC: number;
  ffp: FfpType;
}

interface IVisibilityOptionsRoles {
  admin: boolean;
  teamLead: boolean;
  phlebotomist: boolean;
}

interface IPhlebotomistDashboardSettingsJson {
  showDebug: boolean;
  failureWeights: IDonationFailureWeight[];
  pendingTimeoutHrs: number;
  visibilityOptionsRoles: IVisibilityOptionsRoles;
  excludedUserIds: string[];
  simulateRole: UserRole | null;
  simulateUserId: string | null;
}

const DEFAULT_FAILURE_WEIGHTS: IDonationFailureWeight[] = [
  { minMl: 0, maxMl: 420, pRBC: 1, ffp: 'SGL' },
  { minMl: 421, maxMl: 1000, pRBC: 2, ffp: 'DBL' },
];

const DEFAULT_PENDING_TIMEOUT_HRS = 24;

const DEFAULT_VISIBILITY_OPTIONS_ROLES: IVisibilityOptionsRoles = {
  admin: true,
  teamLead: true,
  phlebotomist: true,
};

const DEFAULT_SETTINGS: IPhlebotomistDashboardSettingsJson = {
  showDebug: false,
  failureWeights: DEFAULT_FAILURE_WEIGHTS,
  pendingTimeoutHrs: DEFAULT_PENDING_TIMEOUT_HRS,
  visibilityOptionsRoles: DEFAULT_VISIBILITY_OPTIONS_ROLES,
  excludedUserIds: [],
  simulateRole: null,
  simulateUserId: null,
};

const ALL_USER_ROLES: UserRole[] = Object.values(UserRole);

const SETTINGS_TAB = 'phlebotomist-dashboard';

const MAX_ML = 1000;
const MIN_SEGMENT_ML = 20;
const MAX_RANGES = 8;
const MIN_RANGES = 2;

const SEGMENT_COLORS = [
  '#0e4d8f', // Deep Navy
  '#1565c0', // Royal Blue
  '#f31a25', // Red
  '#4f6d7a', // Warm Beige
  '#6e7a8a', // Cool Mint
  '#0b345f', // Steel Blue
  '#2f3a45', // Slate Gray
  '#a7b4c2', // Light Gray
];

const PhlebotomistDashboardSettings: FC = () => {
  const { t } = useTranslation('settings.PhlebotomistDashboardSettings');

  const [showDebug, setShowDebug] = useState(DEFAULT_SETTINGS.showDebug);
  const [failureWeights, setFailureWeights] = useState<
    IDonationFailureWeight[]
  >(DEFAULT_FAILURE_WEIGHTS);
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState<
    number | null
  >(null);
  const [pendingTimeoutHrs, setPendingTimeoutHrs] = useState(
    DEFAULT_PENDING_TIMEOUT_HRS
  );
  const [visibilityOptionsRoles, setVisibilityOptionsRoles] =
    useState<IVisibilityOptionsRoles>(DEFAULT_VISIBILITY_OPTIONS_ROLES);
  const [userViewOptions, setUserViewOptions] = useState<
    Record<string, unknown> | undefined
  >(undefined);
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);
  // Admin simulate user role
  const [simulateRole, setSimulateRole] = useState<UserRole | null>(null);
  const [simulateUserId, setSimulateUserId] = useState<string | null>(null);
  const [isModified, setIsModified] = useState(false);

  // Fetch users by selected simulation role
  const [fetchSimulateUsers, simulateUsersQuery] = useGetUsersListLazyQuery();

  useEffect(() => {
    if (simulateRole && simulateRole !== UserRole.Admin) {
      fetchSimulateUsers({
        variables: {
          input: {
            filter: { roles: [simulateRole], isArchived: false },
            take: 200,
          },
        },
      });
    }
  }, [simulateRole, fetchSimulateUsers]);

  const simulateUserOptions = useMemo(() => {
    if (!simulateUsersQuery.data?.getUsersList?.data) return [];
    return simulateUsersQuery.data.getUsersList.data
      .map((user) => {
        const name = `${user.firstName} ${user.lastName}`;
        const regionText = user.productRegionPreference?.name || 'No region';
        const officeText = user.assignedOffice?.name || 'No office';
        return {
          value: user.id,
          searchLabel: `${name} ${regionText} ${officeText}`,
          label: (
            <span>
              {name} <Text type="secondary">({regionText})</Text>{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                [{officeText}]
              </Text>
            </span>
          ),
        };
      })
      .sort((a, b) => a.searchLabel.localeCompare(b.searchLabel));
  }, [simulateUsersQuery.data]);

  // Fetch Phlebotomist/TeamLead users for the exclude multi-select
  const usersQuery = useGetUsersListQuery({
    variables: {
      input: {
        filter: {
          roles: [UserRole.Phlebotomist, UserRole.TeamLead],
          isArchived: false,
        },
        take: 100,
      },
    },
  });

  const userOptions = useMemo(() => {
    if (!usersQuery.data?.getUsersList.data) return [];
    return usersQuery.data.getUsersList.data
      .map((user) => {
        const name = `${user.firstName} ${user.lastName}`;
        const regionText = user.productRegionPreference?.name || 'No region';
        const officeText = user.assignedOffice?.name || 'No office';
        return {
          value: user.id,
          searchLabel: `${name} ${regionText} ${officeText}`,
          label: (
            <span>
              {name} <Text type="secondary">({regionText})</Text>{' '}
              <Text type="secondary" style={{ fontSize: 12 }}>
                [{officeText}]
              </Text>
            </span>
          ),
        };
      })
      .sort((a, b) => a.searchLabel.localeCompare(b.searchLabel));
  }, [usersQuery.data]);

  const { loading: isLoading } = useQuery(GET_APP_SETTINGS_QUERY, {
    variables: { input: { settingsTab: SETTINGS_TAB } },
    onCompleted: (data) => {
      if (data?.getAppSettings?.settingsJson) {
        const saved = data.getAppSettings
          .settingsJson as IPhlebotomistDashboardSettingsJson;
        if (saved.showDebug !== undefined) setShowDebug(saved.showDebug);
        if (saved.failureWeights?.length) {
          // Normalize legacy overlapping boundaries (minMl === prev.maxMl → minMl = prev.maxMl + 1)
          const normalized = saved.failureWeights.map((w, i) => {
            if (i === 0) return w;
            const prevMax = saved.failureWeights[i - 1].maxMl;
            return w.minMl <= prevMax ? { ...w, minMl: prevMax + 1 } : w;
          });
          setFailureWeights(normalized);
        }
        if (saved.pendingTimeoutHrs !== undefined)
          setPendingTimeoutHrs(saved.pendingTimeoutHrs);
        if (saved.visibilityOptionsRoles)
          setVisibilityOptionsRoles({
            ...DEFAULT_VISIBILITY_OPTIONS_ROLES,
            ...saved.visibilityOptionsRoles,
          });
        if (saved.excludedUserIds?.length)
          setExcludedUserIds(saved.excludedUserIds);
        if ((saved as Record<string, unknown>).userViewOptions)
          setUserViewOptions(
            (saved as Record<string, unknown>).userViewOptions as Record<
              string,
              unknown
            >
          );
        if (saved.simulateRole !== undefined)
          setSimulateRole(saved.simulateRole);
        if (saved.simulateUserId !== undefined)
          setSimulateUserId(saved.simulateUserId);
      }
    },
  });

  const [saveAppSettings, { loading: isSaving }] = useMutation(
    SAVE_APP_SETTINGS_MUTATION,
    {
      onCompleted: () => {
        message.success(t('settingsSaved', 'Settings saved successfully'));
      },
      onError: (error) => {
        message.error(`Failed to save settings: ${error.message}`);
      },
    }
  );

  const currentSettings = useMemo(
    () => ({
      showDebug,
      failureWeights,
      pendingTimeoutHrs,
      visibilityOptionsRoles,
      excludedUserIds,
      simulateRole,
      simulateUserId,
      ...(userViewOptions ? { userViewOptions } : {}),
    }),
    [
      showDebug,
      failureWeights,
      pendingTimeoutHrs,
      visibilityOptionsRoles,
      excludedUserIds,
      simulateRole,
      simulateUserId,
      userViewOptions,
    ]
  );

  const hasUnsavedChanges = isModified;

  const handleSave = async () => {
    try {
      await saveAppSettings({
        variables: {
          input: {
            settingsTab: SETTINGS_TAB,
            settingsJson: currentSettings,
          },
        },
      });
      setIsModified(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const markModified = () => setIsModified(true);

  const handleAddRange = () => {
    if (failureWeights.length >= MAX_RANGES) return;
    markModified();

    let widestIndex = 0;
    let widestWidth = 0;
    failureWeights.forEach((w, i) => {
      const width = w.maxMl - w.minMl;
      if (width > widestWidth) {
        widestWidth = width;
        widestIndex = i;
      }
    });

    const existing = failureWeights[widestIndex];
    const midpoint = Math.round((existing.minMl + existing.maxMl) / 2);

    const newWeights = [
      ...failureWeights.slice(0, widestIndex),
      { ...existing, maxMl: midpoint },
      {
        minMl: midpoint + 1,
        maxMl: existing.maxMl,
        pRBC: existing.pRBC,
        ffp: existing.ffp,
      },
      ...failureWeights.slice(widestIndex + 1),
    ];

    setFailureWeights(newWeights);
    setSelectedSegmentIndex(widestIndex + 1);
  };

  const handleDeleteRange = (index: number) => {
    if (failureWeights.length <= MIN_RANGES) return;
    markModified();

    const deleted = failureWeights[index];
    const updated = [...failureWeights];

    if (index === 0) {
      updated[1] = { ...updated[1], minMl: 0 };
    } else {
      updated[index - 1] = { ...updated[index - 1], maxMl: deleted.maxMl };
    }
    updated.splice(index, 1);

    setFailureWeights(updated);
    setSelectedSegmentIndex(null);
  };

  const handleDrag = (handleIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startValue = failureWeights[handleIndex].maxMl;
    const container = (e.currentTarget as HTMLElement).parentElement;
    if (!container) return;
    const containerWidth = container.offsetWidth;

    const lowerBound =
      handleIndex === 0
        ? MIN_SEGMENT_ML
        : failureWeights[handleIndex].minMl + MIN_SEGMENT_ML;

    const upperBound =
      handleIndex === failureWeights.length - 2
        ? MAX_ML - MIN_SEGMENT_ML
        : failureWeights[handleIndex + 1].maxMl - MIN_SEGMENT_ML;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const deltaMl = (delta / containerWidth) * MAX_ML;
      const newValue = Math.round(
        Math.min(Math.max(lowerBound, startValue + deltaMl), upperBound)
      );

      markModified();
      setFailureWeights((prev) => {
        const updated = [...prev];
        updated[handleIndex] = { ...updated[handleIndex], maxMl: newValue };
        updated[handleIndex + 1] = {
          ...updated[handleIndex + 1],
          minMl: newValue + 1,
        };
        return updated;
      });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card>
          <Title level={5}>{t('debugTitle', 'Debug Settings')}</Title>
          <Text type="secondary">
            {t(
              'debugDescription',
              'Show debug information on the phlebotomist dashboard.'
            )}
          </Text>

          <div style={{ marginTop: 16 }}>
            <Checkbox
              checked={showDebug}
              onChange={(e) => {
                markModified();
                setShowDebug(e.target.checked);
              }}
            >
              {t('showDebugLabel', 'Show Debug')}
            </Checkbox>
          </div>
        </Card>
      </Col>

      {/* Admin Simulate User Role */}
      <Col span={24}>
        <Card>
          <Title level={5}>Admin Simulate User Role</Title>
          <Text type="secondary">
            Simulate viewing this dashboard as a different user role. When a
            non-Admin role is selected, pick a specific user to impersonate
            their context (office, region, etc.). This is for testing role-based
            behavior.
          </Text>

          <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Simulate Role
            </Text>
            <Select
              value={simulateRole}
              onChange={(value: UserRole | null) => {
                markModified();
                setSimulateRole(value ?? null);
                setSimulateUserId(null);
              }}
              style={{ width: '100%' }}
              allowClear
              placeholder="No simulation (Admin)"
            >
              {ALL_USER_ROLES.map((role) => (
                <Select.Option key={role} value={role}>
                  {role}
                </Select.Option>
              ))}
            </Select>
          </div>

          {simulateRole && simulateRole !== UserRole.Admin && (
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                Simulate User
              </Text>
              <Text
                type="secondary"
                style={{ display: 'block', fontSize: 12, marginBottom: 8 }}
              >
                Pick a specific user with the {simulateRole} role to impersonate
                their context (office, region, etc.)
              </Text>
              <Select
                value={simulateUserId}
                onChange={(value: string) => {
                  markModified();
                  setSimulateUserId(value ?? null);
                }}
                style={{ width: '100%' }}
                allowClear
                showSearch
                loading={simulateUsersQuery.loading}
                placeholder="Select a user to simulate..."
                filterOption={(input, option) =>
                  ((option as Record<string, unknown>)?.searchLabel as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase()) ?? false
                }
                options={simulateUserOptions}
              />
            </div>
          )}
        </Card>
      </Col>

      {/* Pending Processing Timeout */}
      <Col span={24}>
        <Card>
          <Title level={5}>
            {t(
              'pendingTimeoutTitle',
              'Pending Processing Donation Status Timeout'
            )}
          </Title>
          <Text type="secondary">
            {t(
              'pendingTimeoutDescription',
              'Donations pending processing longer than this threshold will be marked as Failed (as "Unsuccessful", or "Low Volume" if volume thresholds are met).'
            )}
          </Text>

          <div
            style={{
              marginTop: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Text>{t('pendingTimeoutLabel', 'Timeout (hours):')}</Text>
            <InputNumber
              value={pendingTimeoutHrs}
              onChange={(value) => {
                markModified();
                setPendingTimeoutHrs(value ?? DEFAULT_PENDING_TIMEOUT_HRS);
              }}
              min={1}
              max={720}
              precision={0}
              style={{ width: 80 }}
            />
          </div>
        </Card>
      </Col>

      {/* Show Visibility Options */}
      <Col span={24}>
        <Card>
          <Title level={5}>
            {t('visibilityOptionsTitle', 'Show Visibility Options')}
          </Title>
          <Text type="secondary">
            {t(
              'visibilityOptionsDescription',
              'Control which roles can see the Visibility Options accordion on the dashboard. Roles without access will see all sections by default.'
            )}
          </Text>

          <div
            style={{
              marginTop: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <Checkbox
              checked={visibilityOptionsRoles.admin}
              onChange={(e) => {
                markModified();
                setVisibilityOptionsRoles((prev) => ({
                  ...prev,
                  admin: e.target.checked,
                }));
              }}
            >
              {t('visibilityRoleAdmin', 'Admin / Medical Director / DVM')}
            </Checkbox>
            <Checkbox
              checked={visibilityOptionsRoles.teamLead}
              onChange={(e) => {
                markModified();
                setVisibilityOptionsRoles((prev) => ({
                  ...prev,
                  teamLead: e.target.checked,
                }));
              }}
            >
              {t('visibilityRoleTeamLead', 'Team Lead / Collection Team')}
            </Checkbox>
            <Checkbox
              checked={visibilityOptionsRoles.phlebotomist}
              onChange={(e) => {
                markModified();
                setVisibilityOptionsRoles((prev) => ({
                  ...prev,
                  phlebotomist: e.target.checked,
                }));
              }}
            >
              {t('visibilityRolePhlebotomist', 'Phlebotomist')}
            </Checkbox>
          </div>
        </Card>
      </Col>

      {/* Exclude Phlebotomists / Team Leads */}
      <Col span={24}>
        <Card>
          <Title level={5}>
            {t('excludeUsersTitle', 'Exclude Phlebotomists / Team Leads')}
          </Title>
          <Text type="secondary">
            {t(
              'excludeUsersDescription',
              'Select phlebotomists or team leads to exclude from all dashboard calculations, aggregations, and the phlebotomist filter dropdown.'
            )}
          </Text>

          <div style={{ marginTop: 16 }}>
            <Select
              mode="multiple"
              value={excludedUserIds}
              onChange={(value: string[]) => {
                markModified();
                setExcludedUserIds(value);
              }}
              options={userOptions}
              loading={usersQuery.loading}
              style={{ width: '100%' }}
              placeholder={t(
                'excludeUsersPlaceholder',
                'Select users to exclude...'
              )}
              showSearch
              filterOption={(input, option) =>
                ((option as any)?.searchLabel as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase()) ?? false
              }
              allowClear
            />
          </div>
        </Card>
      </Col>

      {/* Donation Failure Weights mL */}
      <Col span={24}>
        <Card>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              {t('failureWeightsTitle', 'Donation Failure Weights mL')}
            </Title>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAddRange}
              disabled={failureWeights.length >= MAX_RANGES}
            >
              {t('addRange', 'Add Range')}
            </Button>
          </div>
          <Text type="secondary">
            {t(
              'failureWeightsDescription',
              'Define expected product yields per mL range. Drag handles to adjust breakpoints, click a segment to edit its values.'
            )}
          </Text>

          <div style={{ marginTop: 24, padding: '0 8px' }}>
            {/* Slider bar */}
            <div
              style={{
                position: 'relative',
                height: 32,
                marginTop: 10,
                marginBottom: 10,
              }}
            >
              {/* Colored segments */}
              <div
                style={{
                  display: 'flex',
                  height: '100%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer',
                }}
              >
                {failureWeights.map((weight, index) => {
                  const widthPercent =
                    ((weight.maxMl - weight.minMl) / MAX_ML) * 100;
                  const isSelected = selectedSegmentIndex === index;
                  return (
                    <div
                      key={index}
                      style={{
                        width: `${widthPercent}%`,
                        backgroundColor:
                          SEGMENT_COLORS[index % SEGMENT_COLORS.length],
                        height: '100%',
                        opacity: isSelected ? 1 : 0.7,
                        outline: isSelected ? '2px solid #fff' : undefined,
                        outlineOffset: -2,
                        transition: 'opacity 0.15s',
                      }}
                      onClick={() => setSelectedSegmentIndex(index)}
                    />
                  );
                })}
              </div>

              {/* Drag handles at breakpoints */}
              {failureWeights.slice(0, -1).map((weight, index) => (
                <div
                  key={`handle-${index}`}
                  style={{
                    position: 'absolute',
                    left: `${(weight.maxMl / MAX_ML) * 100}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 15,
                    height: 40,
                    borderRadius: 4,
                    backgroundColor: '#4f6d7a',
                    border: '2px solid #fff',
                    cursor: 'ew-resize',
                    zIndex: 2,
                  }}
                  onMouseDown={(e) => handleDrag(index, e)}
                />
              ))}
            </div>

            {/* Labels below the bar */}
            <div
              style={{
                display: 'flex',
                position: 'relative',
                minHeight: 44,
                marginTop: 8,
              }}
            >
              {failureWeights.map((weight, index) => {
                const widthPercent =
                  ((weight.maxMl - weight.minMl) / MAX_ML) * 100;
                const isSelected = selectedSegmentIndex === index;
                return (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      left: `${(weight.minMl / MAX_ML) * 100}%`,
                      width: `${widthPercent}%`,
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedSegmentIndex(index)}
                  >
                    <Text
                      style={{
                        display: 'block',
                        fontSize: 12,
                        fontWeight: isSelected ? 700 : 600,
                      }}
                    >
                      {weight.minMl} - {weight.maxMl} mL
                    </Text>
                    <Text
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: '#666',
                      }}
                    >
                      {weight.pRBC} pRBC, {weight.ffp} FFP
                    </Text>
                  </div>
                );
              })}
            </div>

            {/* Editing panel for selected segment */}
            {selectedSegmentIndex !== null &&
              selectedSegmentIndex < failureWeights.length && (
                <Card
                  size="small"
                  style={{
                    marginTop: 16,
                    border: `2px solid ${
                      SEGMENT_COLORS[
                        selectedSegmentIndex % SEGMENT_COLORS.length
                      ]
                    }`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 12,
                    }}
                  >
                    <Text strong>
                      Editing: {failureWeights[selectedSegmentIndex].minMl} -{' '}
                      {failureWeights[selectedSegmentIndex].maxMl} mL
                    </Text>
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={failureWeights.length <= MIN_RANGES}
                      onClick={() => handleDeleteRange(selectedSegmentIndex)}
                    >
                      {t('deleteRange', 'Delete Range')}
                    </Button>
                  </div>
                  <Space size={24}>
                    <Space>
                      <Text>pRBC:</Text>
                      <InputNumber
                        value={failureWeights[selectedSegmentIndex].pRBC}
                        onChange={(value) => {
                          markModified();
                          setFailureWeights((prev) => {
                            const updated = [...prev];
                            updated[selectedSegmentIndex] = {
                              ...updated[selectedSegmentIndex],
                              pRBC: value ?? 0,
                            };
                            return updated;
                          });
                        }}
                        min={0}
                        max={5}
                        precision={0}
                        style={{ width: 60 }}
                      />
                    </Space>
                    <Space>
                      <Text>FFP:</Text>
                      <Select
                        value={failureWeights[selectedSegmentIndex].ffp}
                        onChange={(value: FfpType) => {
                          markModified();
                          setFailureWeights((prev) => {
                            const updated = [...prev];
                            updated[selectedSegmentIndex] = {
                              ...updated[selectedSegmentIndex],
                              ffp: value,
                            };
                            return updated;
                          });
                        }}
                        style={{ width: 100 }}
                        options={[
                          { label: 'None', value: 'None' },
                          { label: 'SGL', value: 'SGL' },
                          { label: 'DBL', value: 'DBL' },
                        ]}
                      />
                    </Space>
                  </Space>
                </Card>
              )}
          </div>
        </Card>
      </Col>

      <Col span={24}>
        <Card>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 16 }}
          >
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={isSaving}
                disabled={!hasUnsavedChanges}
              >
                {t('saveSettings', 'Save Settings')}
              </Button>
              {hasUnsavedChanges && (
                <Text style={{ color: '#f31a25', fontWeight: 500 }}>
                  ({t('notSaved', 'Not Saved')})
                </Text>
              )}
            </Space>
          </Row>

          <Collapse>
            <Collapse.Panel
              key="settings-json"
              header={
                <Space>
                  <span>{t('settingsJsonLabel', 'Settings JSON')}</span>
                  {hasUnsavedChanges && (
                    <span style={{ color: '#f31a25', fontSize: 12 }}>
                      ({t('notSaved', 'Not Saved')})
                    </span>
                  )}
                </Space>
              }
            >
              <pre
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  fontSize: 12,
                  margin: 0,
                }}
              >
                {JSON.stringify(currentSettings, null, 2)}
              </pre>
            </Collapse.Panel>
          </Collapse>
        </Card>
      </Col>
    </Row>
  );
};

export default PhlebotomistDashboardSettings;
